import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import CheckmarkOutline16 from '@carbon/icons-react/es/checkmark--outline/16';
import View16 from '@carbon/icons-react/es/view/16';
import ViewOff16 from '@carbon/icons-react/es/view--off/16';
import Warning16 from '@carbon/icons-react/es/warning/16';
import { OktaContext } from '@okta/okta-react';
import { Link, useLocationPathname, useLocationSearch, useNavigate } from '@rocketlane/rocket-router';
import { Button, Select } from '@rocketlane/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
// eslint-disable-next-line no-restricted-imports
import { Col, Image, Row, Space, Form as AntdForm } from 'antd';
import { orderBy } from 'lodash';
import React, { Suspense, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AccountAPI, AuthenticationServiceProvider, IdpType } from 'api/account';
import { PermissionSetType, UserType } from 'api/models';
import { UserAPI } from 'api/user';
import { Container, Heading, LinkText, RowHelp, UserInfo } from 'components/atoms/form-elements';
import { Layout } from 'components/atoms/layout';
import { PageLoading } from 'components/atoms/loading';
import { selectPrefix } from 'components/atoms/select';
import { ReactComponent as Google } from 'components/atoms/vendor-loign/google.svg';
import { Input } from 'components/facade/input';
import { toastService } from 'components/molecules/toast';
import { useCognitoAuth } from 'components/organisms/login/use-cognito-auth';
import { useIsSamlMandatory, getEnabledIdp } from 'components/organisms/saml';
import { TokenErrorStatus } from 'components/organisms/token-error/constant';
import { HelperText } from 'components/styles';
import { RoutePaths } from 'constant';
import { RolesApi } from 'data/roles';
import { useCustomAuth } from 'hooks/use-custom-auth';
import { useQueryParams } from 'hooks/use-query-params';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useAccountMetaQuery } from 'queries';
import { logger } from 'services';
import { useDismissBootSkeleton } from 'utils/dismiss-boot-skeleton';
import { getPasswordFormErrorMessage } from 'utils/error';
import { logout } from 'utils/logout';
import { redirectToOAuth, redirectToOAuthWithMagicLink } from 'utils/oauth-redirect';
import { getDecodedEmail, getTokenMeta, isLinkMagicDecoded, isLinkMagicToken, TokenLinkType } from 'utils/token';
import * as styles from './input.module.css';
import logo from './rl_logo_colored.svg';
const TokenError = /*#__PURE__*/ React.lazy(()=>import('components/organisms/token-error/token-error').then((d)=>({
            default: d.TokenError
        })));
var PasswordsFields = /*#__PURE__*/ function(PasswordsFields) {
    PasswordsFields["New"] = "newPassword";
    PasswordsFields["Confirm"] = "confirmPassword";
    return PasswordsFields;
}(PasswordsFields || {});
var FormFields = /*#__PURE__*/ function(FormFields) {
    FormFields["FName"] = "firstName";
    FormFields["LName"] = "lastName";
    FormFields["Role"] = "role";
    return FormFields;
}(FormFields || {});
const StyledSpace = styled(Space)`
  width: 33.5rem;
`;
const Form = styled(AntdForm)`
  .ant-form-item.ant-form-item-has-error {
    .${selectPrefix}__control {
      outline-color: var(--error-color) !important;
    }
  }
`;
const FormItem = styled(AntdForm.Item)`
  padding-bottom: var(--spacing-05);
  &:not(.ant-form-item-with-help) {
    margin-bottom: var(--spacing-05);
  }

  .ant-form-item-explain,
  .ant-form-item-extra {
    ${HelperText};

    &.ant-form-item-explain-error {
      color: var(--error-color);
    }
  }
`;
const StyledRowHelp = styled(RowHelp)`
  flex: 0 0 100%;
  margin-bottom: var(--spacing-05);
`;
const DividerWrapper = styled.span`
  display: flex;
  gap: var(--spacing-03);
  align-items: center;
  padding-bottom: var(--spacing-05);
  span {
    width: 35ch;
    background-color: var(--black-100);
    opacity: 0.1;
    height: 1px;
  }
`;
const StyledWarningIcon = styled(Warning16)`
  color: var(--red-60);
`;
const StyledCheckmarkIcon = styled(CheckmarkOutline16)`
  color: var(--green-60);
`;
const StyledUserInfo = styled(UserInfo)`
  margin: var(--spacing-03) 0 var(--spacing-04) 0;
`;
export const SetupProfilePage = ()=>{
    var _ref;
    var _accountMeta_okta, _data_sessionPayload_user_role, _data_sessionPayload_user, _data_sessionPayload, _data_sessionPayload_user1, _data_sessionPayload1, _data_sessionPayload2, _data_sessionPayload_permissionSet, _data_sessionPayload3, _data_sessionPayload_user_role1, _data_sessionPayload_user2, _data_sessionPayload4, _data_sessionPayload_permissionSet1, _data_sessionPayload5, _data_sessionPayload_user_role2, _data_sessionPayload_user3, _data_sessionPayload6;
    // Public route — dismiss the static #rl-boot-skeleton overlay from
    // public/index.html so it doesn't bleed through this page (or the
    // <TokenError /> branch when token is invalid). See Login.
    useDismissBootSkeleton();
    const oktaContext = useContext(OktaContext);
    const { isAuthenticated: customIsAuthenticated } = useCustomAuth();
    const authState = (_ref = oktaContext === null || oktaContext === void 0 ? void 0 : oktaContext.authState) !== null && _ref !== void 0 ? _ref : {
        isAuthenticated: customIsAuthenticated
    };
    const oktaAuth = oktaContext === null || oktaContext === void 0 ? void 0 : oktaContext.oktaAuth;
    const isAuthenticated = authState === null || authState === void 0 ? void 0 : authState.isAuthenticated;
    const { token, tokenManager } = oktaAuth !== null && oktaAuth !== void 0 ? oktaAuth : {};
    const pathname = useLocationPathname();
    const search = useLocationSearch();
    const replace = useNavigate();
    const redirect_uri = pathname + search;
    const [continueWithPassword, setContinueWithPassword] = useState(false);
    const [passwordToggle, setPasswordToggle] = useState({
        type: 'password',
        icon: ViewOff16
    });
    const [warningVisibility, setWarningVisibility] = useState(false);
    const [successVisbility, setSuccessVisibility] = useState(false);
    const { data: accountMeta } = useAccountMetaQuery();
    const isCognitoEnabled = (accountMeta === null || accountMeta === void 0 ? void 0 : (_accountMeta_okta = accountMeta.okta) === null || _accountMeta_okta === void 0 ? void 0 : _accountMeta_okta.authenticationServiceProvider) === AuthenticationServiceProvider.COGNITO;
    const params = useQueryParams();
    const urlToken = params.get('token') || '';
    const { data, isLoading } = useQuery([
        'get-details-from-token',
        urlToken
    ], ()=>UserAPI.getDetailsFromToken(urlToken), {
        retry: false,
        refetchOnWindowFocus: false,
        enabled: !isAuthenticated
    });
    const isPartnerUser = (data === null || data === void 0 ? void 0 : data.sessionPayload.user.userType) === UserType.Partner;
    const isPartnerAdmin = isPartnerUser && data.sessionPayload.permissionSet.permissionSetType === PermissionSetType.PartnerAdmin;
    // TODO: when we add sso for partner, make sure role field is filled up before proceeding to sso
    const isSamlEnabled = useMemo(()=>{
        var _ref;
        return !!((_ref = accountMeta === null || accountMeta === void 0 ? void 0 : accountMeta.okta.idps) !== null && _ref !== void 0 ? _ref : []).filter((idp)=>idp.enabled).length;
    }, [
        accountMeta === null || accountMeta === void 0 ? void 0 : accountMeta.okta.idps
    ]);
    const isSSoEnabled = isPartnerUser ? false : isSamlEnabled;
    const isSamlMandate = useIsSamlMandatory();
    const { data: roles, isLoading: isRoleLoading } = useQuery([
        'roles_from_token',
        urlToken
    ], ()=>RolesApi.findAllWithToken(urlToken), {
        retry: false,
        refetchOnWindowFocus: false,
        enabled: !isAuthenticated
    });
    const roleOptions = useMemo(()=>{
        var _ref;
        const filteredRoles = (_ref = (data === null || data === void 0 ? void 0 : data.sessionPayload.user.userType) === UserType.Partner ? roles === null || roles === void 0 ? void 0 : roles.filter((role)=>role.usedByPartners === true) : roles) !== null && _ref !== void 0 ? _ref : [];
        return orderBy(filteredRoles.map(({ roleName, roleId })=>({
                label: roleName,
                value: roleId
            })), [
            ({ label })=>label.toLowerCase()
        ], [
            'asc'
        ]);
    }, [
        data === null || data === void 0 ? void 0 : data.sessionPayload.user.userType,
        roles
    ]);
    const initialRole = useMemo(()=>{
        var _data_sessionPayload_user_role;
        const roleId = data === null || data === void 0 ? void 0 : (_data_sessionPayload_user_role = data.sessionPayload.user.role) === null || _data_sessionPayload_user_role === void 0 ? void 0 : _data_sessionPayload_user_role.roleId;
        return roleOptions.find((option)=>option.value === roleId);
    }, [
        data === null || data === void 0 ? void 0 : (_data_sessionPayload_user_role = data.sessionPayload.user.role) === null || _data_sessionPayload_user_role === void 0 ? void 0 : _data_sessionPayload_user_role.roleId,
        roleOptions
    ]);
    /**
   * if the user is authenticated, we will logout the old token
   * and then process the new token.
   * Since the earlier implementation followed redirect flow, it wasnt an issue.
   * In the latest implementation since we are preventing redirects to prevent tab refresh
   * to cut down unnecessary API calls
   * we maintain a state to identify if it is new token or not
   * to prevent logout of new token once authenticated.
   */ const [isNewToken, setIsNewToken] = useState(false);
    useEffect(()=>{
        if (isAuthenticated) {
            if (!isNewToken) {
                logout(redirect_uri);
            } else {
                replace('/', {
                    replace: true
                });
            }
        }
    }, [
        isAuthenticated,
        isNewToken,
        redirect_uri,
        replace
    ]);
    useEffect(()=>{
        if (isSSoEnabled) {
            return;
        }
        setContinueWithPassword(true);
    }, [
        isSSoEnabled
    ]);
    const updateProfile = useCallback((values)=>{
        setIsNewToken(true);
        return UserAPI.setupProfileWithToken({
            firstName: values.firstName || '',
            lastName: values.lastName || '',
            newPassword: values.newPassword || '',
            confirmPassword: values.confirmPassword || '',
            token: urlToken || ''
        }).then((resp)=>{
            var _accountMeta_okta;
            if (isCognitoEnabled) {
                window.location.href = `${window.location.origin}/login?token=${urlToken}`;
            } else if (accountMeta === null || accountMeta === void 0 ? void 0 : (_accountMeta_okta = accountMeta.okta) === null || _accountMeta_okta === void 0 ? void 0 : _accountMeta_okta.cookiesEnabled) {
                var _accountMeta_okta1;
                if (accountMeta === null || accountMeta === void 0 ? void 0 : (_accountMeta_okta1 = accountMeta.okta) === null || _accountMeta_okta1 === void 0 ? void 0 : _accountMeta_okta1.clientId) {
                    // Auto-login by replaying the same LINK_MAGIC through id PKCE — no re-entry.
                    if (isLinkMagicToken(urlToken)) {
                        redirectToOAuthWithMagicLink(accountMeta.okta.clientId, urlToken);
                    } else {
                        redirectToOAuth(accountMeta.okta.clientId);
                    }
                }
            } else if (token) {
                return token.getWithoutPrompt({
                    sessionToken: resp.sessionToken
                }).then((res)=>{
                    tokenManager === null || tokenManager === void 0 ? void 0 : tokenManager.setTokens(res.tokens);
                }).catch((error)=>{
                    logger.error('Token setup failed', {
                        error
                    });
                    return Promise.reject(error);
                });
            }
            return Promise.resolve();
        }).catch((error)=>Promise.reject(error));
    }, [
        isCognitoEnabled,
        token,
        tokenManager,
        urlToken,
        accountMeta
    ]);
    const updateUser = useCallback(({ firstName, lastName, roleId })=>{
        if (!data) {
            return;
        }
        const payload = {
            userId: data.sessionPayload.user.userId
        };
        if (firstName) {
            payload.firstName = firstName;
        }
        if (lastName) {
            payload.lastName = lastName;
        }
        if (roleId) {
            payload.role = {
                roleId
            };
        }
        UserAPI.save(payload, urlToken);
    }, [
        data,
        urlToken
    ]);
    const { mutateAsync: setupProfile, isLoading: isSetupProfilePending } = useMutation(updateProfile);
    const passwordMatchText = useTranslation('form.validations.password_match');
    const pwdPlaceholder = useTranslation('password.placeholder');
    const translate = useTranslate();
    const { isExpired, decoded } = getTokenMeta(urlToken);
    const idp = getEnabledIdp(accountMeta === null || accountMeta === void 0 ? void 0 : accountMeta.okta);
    const onFinish = (values)=>{
        setSuccessVisibility(false);
        setupProfile(values).catch((error)=>{
            const { primary, secondary } = getPasswordFormErrorMessage(error, translate('password.validation.unified'), translate('rte.error'));
            toastService({
                title: translate('error.generic.title'),
                message: primary
            }).error();
            if (secondary != null) {
                toastService({
                    title: translate('error.generic.title'),
                    message: secondary
                }).error();
            }
        });
    };
    const { samlFlowHandler, googleSSOHandler } = useCognitoAuth({
        data: accountMeta
    });
    if (!decoded) {
        return /*#__PURE__*/ _jsx(Suspense, {
            fallback: /*#__PURE__*/ _jsx(PageLoading, {}),
            children: /*#__PURE__*/ _jsx(TokenError, {
                status: TokenErrorStatus.Invalid,
                linkType: TokenLinkType.AccountInvitation
            })
        });
    }
    if (decoded && isExpired) {
        const email = getDecodedEmail(decoded);
        return /*#__PURE__*/ _jsx(Suspense, {
            fallback: /*#__PURE__*/ _jsx(PageLoading, {}),
            children: /*#__PURE__*/ _jsx(TokenError, {
                status: TokenErrorStatus.Expired,
                email: email,
                resendEmail: ()=>isLinkMagicToken(urlToken) ? AccountAPI.resendMagicLink(urlToken) : AccountAPI.memberActivateResend({
                        email
                    }),
                linkType: isLinkMagicDecoded(decoded) ? decoded.link_type : decoded.linkType
            })
        });
    }
    return isLoading || isAuthenticated ? /*#__PURE__*/ _jsx(PageLoading, {}) : /*#__PURE__*/ _jsx(Layout, {
        children: /*#__PURE__*/ _jsx(Container, {
            children: /*#__PURE__*/ _jsxs(StyledSpace, {
                direction: "vertical",
                size: 16,
                children: [
                    /*#__PURE__*/ _jsx(Image, {
                        width: 186,
                        src: logo,
                        preview: false
                    }),
                    /*#__PURE__*/ _jsx(Heading, {
                        children: /*#__PURE__*/ _jsx(FormattedMessage, {
                            id: "setup_profile.title"
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Form, {
                        layout: "vertical",
                        name: "set-profile",
                        onFinish: onFinish,
                        children: [
                            /*#__PURE__*/ _jsxs(Row, {
                                gutter: 16,
                                children: [
                                    /*#__PURE__*/ _jsx(Col, {
                                        span: 12,
                                        children: /*#__PURE__*/ _jsx(Form.Item, {
                                            name: "firstName",
                                            label: /*#__PURE__*/ _jsx(FormattedMessage, {
                                                id: "first_name"
                                            }),
                                            initialValue: data === null || data === void 0 ? void 0 : (_data_sessionPayload = data.sessionPayload) === null || _data_sessionPayload === void 0 ? void 0 : (_data_sessionPayload_user = _data_sessionPayload.user) === null || _data_sessionPayload_user === void 0 ? void 0 : _data_sessionPayload_user.firstName,
                                            children: /*#__PURE__*/ _jsx(Input, {
                                                onBlur: (e)=>updateUser({
                                                        firstName: e.target.value
                                                    })
                                            })
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Col, {
                                        span: 12,
                                        children: /*#__PURE__*/ _jsx(Form.Item, {
                                            name: "lastName",
                                            label: /*#__PURE__*/ _jsx(FormattedMessage, {
                                                id: "last_name"
                                            }),
                                            initialValue: data === null || data === void 0 ? void 0 : (_data_sessionPayload1 = data.sessionPayload) === null || _data_sessionPayload1 === void 0 ? void 0 : (_data_sessionPayload_user1 = _data_sessionPayload1.user) === null || _data_sessionPayload_user1 === void 0 ? void 0 : _data_sessionPayload_user1.lastName,
                                            children: /*#__PURE__*/ _jsx(Input, {
                                                onBlur: (e)=>updateUser({
                                                        lastName: e.target.value
                                                    })
                                            })
                                        })
                                    }),
                                    (data === null || data === void 0 ? void 0 : (_data_sessionPayload2 = data.sessionPayload) === null || _data_sessionPayload2 === void 0 ? void 0 : _data_sessionPayload2.permissionSet) ? /*#__PURE__*/ _jsx(Col, {
                                        span: 12,
                                        children: /*#__PURE__*/ _jsx(StyledRowHelp, {
                                            children: isPartnerAdmin ? /*#__PURE__*/ _jsx(FormattedMessage, {
                                                id: "setup_profile.partner_user_access_info",
                                                values: {
                                                    permission: /*#__PURE__*/ _jsx("strong", {
                                                        children: data === null || data === void 0 ? void 0 : (_data_sessionPayload3 = data.sessionPayload) === null || _data_sessionPayload3 === void 0 ? void 0 : (_data_sessionPayload_permissionSet = _data_sessionPayload3.permissionSet) === null || _data_sessionPayload_permissionSet === void 0 ? void 0 : _data_sessionPayload_permissionSet.permissionSetName
                                                    })
                                                }
                                            }) : /*#__PURE__*/ _jsx(FormattedMessage, {
                                                id: "setup_profile.user_access_info",
                                                values: {
                                                    role: /*#__PURE__*/ _jsx("strong", {
                                                        children: data === null || data === void 0 ? void 0 : (_data_sessionPayload4 = data.sessionPayload) === null || _data_sessionPayload4 === void 0 ? void 0 : (_data_sessionPayload_user2 = _data_sessionPayload4.user) === null || _data_sessionPayload_user2 === void 0 ? void 0 : (_data_sessionPayload_user_role1 = _data_sessionPayload_user2.role) === null || _data_sessionPayload_user_role1 === void 0 ? void 0 : _data_sessionPayload_user_role1.roleName
                                                    }),
                                                    permission: /*#__PURE__*/ _jsx("strong", {
                                                        children: data === null || data === void 0 ? void 0 : (_data_sessionPayload5 = data.sessionPayload) === null || _data_sessionPayload5 === void 0 ? void 0 : (_data_sessionPayload_permissionSet1 = _data_sessionPayload5.permissionSet) === null || _data_sessionPayload_permissionSet1 === void 0 ? void 0 : _data_sessionPayload_permissionSet1.permissionSetName
                                                    })
                                                }
                                            })
                                        })
                                    }) : null
                                ]
                            }),
                            isPartnerAdmin ? /*#__PURE__*/ _jsx(Row, {
                                gutter: 16,
                                children: /*#__PURE__*/ _jsx(Col, {
                                    span: 24,
                                    children: /*#__PURE__*/ _jsx(FormItem, {
                                        name: "role",
                                        label: /*#__PURE__*/ _jsx(FormattedMessage, {
                                            id: "role"
                                        }),
                                        initialValue: initialRole,
                                        rules: [
                                            {
                                                message: /*#__PURE__*/ _jsx(FormattedMessage, {
                                                    id: "input.required"
                                                }),
                                                required: isPartnerAdmin
                                            }
                                        ],
                                        children: /*#__PURE__*/ _jsx(Select, {
                                            autoFocus: true,
                                            classNamePrefix: "ozone-select",
                                            options: roleOptions,
                                            isLoading: isRoleLoading,
                                            onChange: (next)=>updateUser({
                                                    roleId: next === null || next === void 0 ? void 0 : next.value
                                                })
                                        })
                                    })
                                })
                            }) : null,
                            isSSoEnabled && /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    idp && /*#__PURE__*/ _jsx(Button, {
                                        type: "button",
                                        leftIcon: idp.idpType === IdpType.Google && /*#__PURE__*/ _jsx(Google, {}),
                                        onClick: ()=>{
                                            if (isCognitoEnabled) {
                                                if (idp.idpType === IdpType.Google) {
                                                    googleSSOHandler();
                                                } else {
                                                    samlFlowHandler();
                                                }
                                                return;
                                            }
                                            oktaAuth === null || oktaAuth === void 0 ? void 0 : oktaAuth.signInWithRedirect({
                                                idp: idp.idpId
                                            });
                                        },
                                        children: idp.idpType === IdpType.Google ? translate('google.sign-up') : translate('setup_profile.sso.button')
                                    }, idp.idpId),
                                    /*#__PURE__*/ _jsx(StyledUserInfo, {
                                        children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                            id: isSamlMandate ? 'sign_in.sso.mandate.hint' : 'seup_profile.sso.info'
                                        })
                                    }),
                                    !isSamlMandate ? /*#__PURE__*/ _jsxs(DividerWrapper, {
                                        children: [
                                            /*#__PURE__*/ _jsx(FormattedMessage, {
                                                id: "or"
                                            }),
                                            /*#__PURE__*/ _jsx("span", {})
                                        ]
                                    }) : null
                                ]
                            }),
                            isSamlMandate && !isPartnerUser ? null : !continueWithPassword ? /*#__PURE__*/ _jsx(Button, {
                                variant: "secondary",
                                type: "button",
                                onClick: ()=>setContinueWithPassword(true),
                                children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                    id: "setup_profile.password.button"
                                })
                            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(FormItem, {
                                        name: "newPassword",
                                        rules: [
                                            {
                                                message: /*#__PURE__*/ _jsx(FormattedMessage, {
                                                    id: "form.validations.new_password"
                                                })
                                            },
                                            {
                                                min: 11,
                                                message: /*#__PURE__*/ _jsx(FormattedMessage, {
                                                    id: "password.help"
                                                }),
                                                validateTrigger: 'onBlur',
                                                required: continueWithPassword
                                            }
                                        ],
                                        label: /*#__PURE__*/ _jsx(FormattedMessage, {
                                            id: "new_password"
                                        }),
                                        children: /*#__PURE__*/ _jsx(Input, {
                                            autoComplete: "new-password",
                                            name: "newPassword",
                                            type: passwordToggle.type,
                                            placeholder: pwdPlaceholder,
                                            className: ` ${successVisbility ? styles.greenBorder : ''}`,
                                            autoFocus: !(isPartnerAdmin && (data === null || data === void 0 ? void 0 : (_data_sessionPayload_user_role2 = data.sessionPayload.user.role) === null || _data_sessionPayload_user_role2 === void 0 ? void 0 : _data_sessionPayload_user_role2.roleId) == null),
                                            suffix: successVisbility ? /*#__PURE__*/ _jsx(StyledCheckmarkIcon, {}) : /*#__PURE__*/ _jsx(passwordToggle.icon, {
                                                className: `${styles.eyeIcon}`,
                                                onClick: ()=>{
                                                    setPasswordToggle(passwordToggle.type === 'password' ? {
                                                        type: 'text',
                                                        icon: View16
                                                    } : {
                                                        type: 'password',
                                                        icon: ViewOff16
                                                    });
                                                }
                                            })
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(FormItem, {
                                        name: "confirmPassword",
                                        label: /*#__PURE__*/ _jsx(FormattedMessage, {
                                            id: "confirm_password"
                                        }),
                                        dependencies: [
                                            "newPassword"
                                        ],
                                        rules: [
                                            ({ getFieldValue })=>({
                                                    validator (_, value) {
                                                        if (getFieldValue('newPassword') && getFieldValue('newPassword') === value) {
                                                            setSuccessVisibility(true);
                                                            setWarningVisibility(false);
                                                            return Promise.resolve();
                                                        }
                                                        if (value === '') {
                                                            if (getFieldValue('newPassword') === '') {
                                                                if (warningVisibility) {
                                                                    setWarningVisibility(false);
                                                                }
                                                                return Promise.resolve();
                                                            }
                                                        } else if (getFieldValue('newPassword') === '') {
                                                            if (successVisbility) {
                                                                setSuccessVisibility(false);
                                                            }
                                                        }
                                                        if (!warningVisibility) {
                                                            setWarningVisibility(true);
                                                        }
                                                        if (successVisbility) {
                                                            setSuccessVisibility(false);
                                                        }
                                                        return Promise.reject(passwordMatchText);
                                                    }
                                                })
                                        ],
                                        children: /*#__PURE__*/ _jsx(Input, {
                                            autoComplete: "new-password",
                                            type: passwordToggle.type,
                                            placeholder: pwdPlaceholder,
                                            className: ` ${warningVisibility ? styles.redBorder : ''} ${successVisbility ? styles.greenBorder : ''}`,
                                            suffix: /*#__PURE__*/ _jsx(_Fragment, {
                                                children: warningVisibility ? /*#__PURE__*/ _jsx(StyledWarningIcon, {}) : successVisbility ? /*#__PURE__*/ _jsx(StyledCheckmarkIcon, {}) : null
                                            })
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        children: /*#__PURE__*/ _jsx(Button, {
                                            type: "submit",
                                            className: `${styles.marginTop}`,
                                            loading: isSetupProfilePending,
                                            variant: "primary",
                                            rightIcon: /*#__PURE__*/ _jsx(ArrowRight16, {}),
                                            children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                                id: "get_started"
                                            })
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(StyledUserInfo, {
                                children: [
                                    /*#__PURE__*/ _jsx(FormattedMessage, {
                                        id: "setup_profile.user_info",
                                        values: {
                                            email: data === null || data === void 0 ? void 0 : (_data_sessionPayload6 = data.sessionPayload) === null || _data_sessionPayload6 === void 0 ? void 0 : (_data_sessionPayload_user3 = _data_sessionPayload6.user) === null || _data_sessionPayload_user3 === void 0 ? void 0 : _data_sessionPayload_user3.emailId
                                        }
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                            id: "setup_profile.user_info_signin",
                                            values: {
                                                a: (chunks)=>/*#__PURE__*/ _jsx(Link, {
                                                        to: RoutePaths.login,
                                                        children: /*#__PURE__*/ _jsx(LinkText, {
                                                            children: chunks
                                                        })
                                                    })
                                            }
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        })
    });
};
